/** InsCoder WANG.J 2026-08-18 B + Generic section sticky stack orchestration **/

(() => {
  'use strict';

  const MAIN_SELECTOR = '#MainContent';
  const STACK_ITEM_CLASS = 'cust-section-stack__item';
  const STACK_AFTER_CLASS = 'cust-section-stack__after';
  const LAYOUT_REQUIRED_CLASS = 'js-requires-layout-on-load';
  const STICKY_SELECTOR = '[data-cust-sticky-stack]';
  const STACK_LAYER_VARIABLE = '--cust-section-stack-z-index';
  const STACK_TOP_VARIABLE = '--cust-section-stack-top';

  const state = {
    stickyWrappers: new Set(),
    afterWrappers: new Set(),
    layoutRequiredWrappers: new Set(),
    layerWrappers: new Set(),
    topWrappers: new Set(),
    frameId: null
  };

  const getMain = () => document.querySelector(MAIN_SELECTOR);

  const clearManagedClasses = () => {
    state.stickyWrappers.forEach((wrapper) => {
      wrapper.classList.remove(STACK_ITEM_CLASS);
    });

    state.afterWrappers.forEach((wrapper) => {
      wrapper.classList.remove(STACK_AFTER_CLASS);
    });

    state.layoutRequiredWrappers.forEach((wrapper) => {
      wrapper.classList.remove(LAYOUT_REQUIRED_CLASS);
    });

    state.layerWrappers.forEach((wrapper) => {
      wrapper.style.removeProperty(STACK_LAYER_VARIABLE);
    });

    state.topWrappers.forEach((wrapper) => {
      wrapper.style.removeProperty(STACK_TOP_VARIABLE);
    });

    state.stickyWrappers.clear();
    state.afterWrappers.clear();
    state.layoutRequiredWrappers.clear();
    state.layerWrappers.clear();
    state.topWrappers.clear();
  };

  const isStickyWrapper = (wrapper) => wrapper.matches(STICKY_SELECTOR) || wrapper.querySelector(STICKY_SELECTOR);

  const getHeaderOffset = () => {
    const headerOffset = Number.parseFloat(
      window.getComputedStyle(document.documentElement).getPropertyValue('--header-and-announcement-height')
    ) || 0;

    return Math.max(0, headerOffset);
  };

  const getStickyTop = (wrapper, headerOffset) => {
    const wrapperHeight = wrapper.getBoundingClientRect().height;

    // Tall sections first scroll normally. Once their bottom reaches the
    // viewport bottom, a negative top keeps that bottom edge pinned so the
    // following sections can still stack over them.
    return Math.min(headerOffset, window.innerHeight - wrapperHeight);
  };

  const markManagedWrapper = (wrapper, layer) => {
    if (!wrapper.classList.contains(LAYOUT_REQUIRED_CLASS)) {
      wrapper.classList.add(LAYOUT_REQUIRED_CLASS);
      state.layoutRequiredWrappers.add(wrapper);
    }

    wrapper.style.setProperty(STACK_LAYER_VARIABLE, String(layer));
    state.layerWrappers.add(wrapper);
  };

  const setStickyTop = (wrapper, top) => {
    wrapper.style.setProperty(STACK_TOP_VARIABLE, `${top}px`);
    state.topWrappers.add(wrapper);
  };

  const refresh = () => {
    const main = getMain();

    clearManagedClasses();

    if (!main) {
      return;
    }

    const children = Array.from(main.children);
    const headerOffset = getHeaderOffset();
    let stickyLayer = 0;
    let hasStickyWrapper = false;

    children.forEach((wrapper) => {
      if (isStickyWrapper(wrapper)) {
        stickyLayer += 1;
        hasStickyWrapper = true;
        wrapper.classList.add(STACK_ITEM_CLASS);
        state.stickyWrappers.add(wrapper);
        markManagedWrapper(wrapper, stickyLayer);
        setStickyTop(wrapper, getStickyTop(wrapper, headerOffset));
        return;
      }

      if (hasStickyWrapper) {
        wrapper.classList.add(STACK_AFTER_CLASS);
        state.afterWrappers.add(wrapper);
        markManagedWrapper(wrapper, stickyLayer + 1);
      }
    });
  };

  const scheduleRefresh = () => {
    if (state.frameId !== null) {
      return;
    }

    state.frameId = window.requestAnimationFrame(() => {
      state.frameId = null;
      refresh();
    });
  };

  const initialize = () => {
    refresh();
    window.addEventListener('resize', scheduleRefresh, { passive: true });
    window.addEventListener('load', scheduleRefresh, { once: true });
    document.addEventListener('shopify:section:load', scheduleRefresh);
    document.addEventListener('shopify:section:unload', scheduleRefresh);
    document.addEventListener('shopify:section:reorder', scheduleRefresh);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();

/** InsCoder WANG.J 2026-08-18 E + Generic section sticky stack orchestration **/
